import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import api, { formatApiErrorDetail } from "@/lib/api";

/** Encapsulates the Dashboard header's cart-switcher dropdown + "New Cart" dialog state. */
export function useDashboardCarts() {
  const { toast } = useToast();

  const [carts, setCarts] = useState([]);
  const [newCartOpen, setNewCartOpen] = useState(false);
  const [newCartName, setNewCartName] = useState("");
  const [creatingCart, setCreatingCart] = useState(false);

  const fetchCarts = useCallback(() => {
    api
      .get("/carts")
      .then(({ data }) => setCarts(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchCarts();
  }, [fetchCarts]);

  const activeCart = carts.find((c) => c.is_active) || null;

  const handleActivateCart = async (cartId) => {
    try {
      await api.put(`/carts/${cartId}/activate`);
      fetchCarts();
      toast({ title: "Cart switched" });
    } catch (e) {
      toast({
        title: "Switch failed",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    }
  };

  const handleCreateCart = async (e) => {
    e.preventDefault();
    if (!newCartName.trim()) return;
    setCreatingCart(true);
    try {
      await api.post("/carts", { customer_name: newCartName.trim() });
      toast({ title: "Cart created" });
      setNewCartName("");
      setNewCartOpen(false);
      fetchCarts();
    } catch (e) {
      toast({
        title: "Failed to create cart",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setCreatingCart(false);
    }
  };

  return {
    carts,
    activeCart,
    newCartOpen,
    setNewCartOpen,
    newCartName,
    setNewCartName,
    creatingCart,
    fetchCarts,
    handleActivateCart,
    handleCreateCart,
  };
}
